import ApisServiceApp from './ApisServiceApp'
import { apiRequest } from '@/utils/apiRequestUtils'
import { blobResponseConfig, ensureExcelBlob, postMultipartFile } from '@/utils/blobApiUtils'
import { normalizeDetailResponse, normalizeListResponse } from '@/utils/apiResponseUtils'
import type {
  INhaCungCap,
  INhaCungCapImportRow,
  INhaCungCapPayload,
  NhaCungCapId,
  NhaCungCapTrangThai,
} from '@/interfaces/nhaCungCapInterfaces'
import queryString from 'query-string'

const PREFIX = '/nha-cung-cap'

function toNum(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (v != null && v !== '') {
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  }
  return null
}

export function mapNhaCungCapFromApi(raw: Record<string, unknown>): INhaCungCap {
  const tt = raw.trangThai
  let trangThai: INhaCungCap['trangThai'] = null
  if (tt === 'dangHoatDong' || tt === 'ngungHoatDong') trangThai = tt
  const chiNhanh = raw.chiNhanh as { ten?: unknown } | null | undefined
  const nguoiTao = raw.nguoiTao as { username?: unknown } | null | undefined
  // sourceSystem='muahang' → đồng bộ từ mua hàng (MH); null/khác → tạo trên bán hàng (BK)
  const phanLoai: INhaCungCap['phanLoai'] =
    raw.sourceSystem === 'muahang' ? 'MH' : 'BK'
  return {
    id: String(raw.id ?? ''),
    tenNhaCungCap: String(raw.tenNhaCungCap ?? raw.ten ?? ''),
    maNhaCungCap: String(raw.maNhaCungCap ?? raw.ma ?? ''),
    phanLoai,
    dienThoai: raw.dienThoai != null ? String(raw.dienThoai) : null,
    email: raw.email != null ? String(raw.email) : null,
    diaChi: raw.diaChi != null ? String(raw.diaChi) : null,
    tinhThanh: raw.tinhThanh != null ? String(raw.tinhThanh) : null,
    phuongXa: raw.phuongXa != null ? String(raw.phuongXa) : null,
    ghiChu: raw.ghiChu != null ? String(raw.ghiChu) : null,
    tenCongTy: raw.tenCongTy != null ? String(raw.tenCongTy) : null,
    maSoThue: raw.maSoThue != null ? String(raw.maSoThue) : null,
    trangThai,
    tongMua: toNum(raw.tongMua),
    noCanTraHienTai: toNum(raw.noCanTraHienTai),
    tongMuaTruTraHang: toNum(raw.tongMuaTruTraHang),
    chiNhanhTen: chiNhanh?.ten != null ? String(chiNhanh.ten) : null,
    nguoiTaoTen: nguoiTao?.username != null ? String(nguoiTao.username) : null,
    ngayTao: raw.ngayTao != null ? String(raw.ngayTao) : null,
    ngayCapNhat: raw.ngayCapNhat != null ? String(raw.ngayCapNhat) : null,
  }
}

/** Ô tùy chọn để trống gửi `''` để BE xóa trắng trường khi sửa. */
function toPayload(body: INhaCungCapPayload) {
  return {
    tenNhaCungCap: body.tenNhaCungCap.trim(),
    maNhaCungCap: body.maNhaCungCap?.trim() || undefined,
    dienThoai: body.dienThoai?.trim() ?? '',
    email: body.email?.trim() ?? '',
    diaChi: body.diaChi?.trim() ?? '',
    tinhThanh: body.tinhThanh?.trim() ?? '',
    phuongXa: body.phuongXa?.trim() ?? '',
    ghiChu: body.ghiChu?.trim() ?? '',
    tenCongTy: body.tenCongTy?.trim() ?? '',
    maSoThue: body.maSoThue?.trim() ?? '',
  }
}

export function nhaCungCapListQueryFromSearch(search: string): Record<string, string | number> {
  const q = queryString.parse(search.replace(/^\?/, ''))
  const out: Record<string, string | number> = {}
  const page = Number(q.page)
  const pageSize = Number(q.pageSize)
  if (Number.isFinite(page) && page > 0) out.page = page
  if (Number.isFinite(pageSize) && pageSize > 0) out.pageSize = pageSize
  const searchVal = q.search
  if (typeof searchVal === 'string' && searchVal.trim()) out.search = searchVal.trim()
  const maNhaCungCap = q.maNhaCungCap
  if (typeof maNhaCungCap === 'string' && maNhaCungCap.trim()) out.maNhaCungCap = maNhaCungCap.trim()
  const tenNhaCungCap = q.tenNhaCungCap
  if (typeof tenNhaCungCap === 'string' && tenNhaCungCap.trim()) out.tenNhaCungCap = tenNhaCungCap.trim()
  const dienThoai = q.dienThoai
  if (typeof dienThoai === 'string' && dienThoai.trim()) out.dienThoai = dienThoai.trim()
  const email = q.email
  if (typeof email === 'string' && email.trim()) out.email = email.trim()
  const khuVuc = q.khuVuc
  if (typeof khuVuc === 'string' && khuVuc.trim()) out.khuVuc = khuVuc.trim()
  const trangThai = q.trangThai
  if (trangThai === 'dangHoatDong' || trangThai === 'ngungHoatDong') out.trangThai = trangThai
  const sort = q.sort
  if (
    sort === 'maNhaCungCap' ||
    sort === 'tenNhaCungCap' ||
    sort === 'ngayTao' ||
    sort === 'trangThai'
  ) {
    out.sort = sort
  }
  const sortDir = q.sortDir
  if (sortDir === 'asc' || sortDir === 'desc') out.sortDir = sortDir
  return out
}

export function fetchNhaCungCapList(search: string) {
  const params = nhaCungCapListQueryFromSearch(search)
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-nha-cung-cap`, { params }).then((res: unknown) => {
      const { data, paging } = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
      return {
        data: data.map((r) => mapNhaCungCapFromApi(r as Record<string, unknown>)),
        paging,
      }
    }),
  )
}

export function fetchNhaCungCapSelect() {
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-nha-cung-cap-select`).then((res: unknown) => {
      const { data } = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
      return data.map((r) => mapNhaCungCapFromApi(r as Record<string, unknown>))
    }),
  )
}

export function fetchNhaCungCapDetail(id: NhaCungCapId) {
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/${id}/chi-tiet-nha-cung-cap`).then((res: unknown) => {
      const raw = normalizeDetailResponse<Record<string, unknown>>(res as { data?: Record<string, unknown> })
      return { data: mapNhaCungCapFromApi(raw) }
    }),
  )
}

export function taoNhaCungCap(body: INhaCungCapPayload) {
  return apiRequest(ApisServiceApp.post(`${PREFIX}/tao-nha-cung-cap`, toPayload(body)))
}

export function capNhatNhaCungCap(id: NhaCungCapId, body: INhaCungCapPayload) {
  return apiRequest(ApisServiceApp.put(`${PREFIX}/${id}/cap-nhat-nha-cung-cap`, toPayload(body)))
}

export function xoaNhaCungCap(id: NhaCungCapId) {
  return apiRequest(ApisServiceApp.delete(`${PREFIX}/${id}/xoa-nha-cung-cap`))
}

export type XoaNhieuNhaCungCapResult = {
  ok: number
  failed: Array<{ id: string; message: string }>
}

/** Xóa nhiều NCC — gọi `DELETE .../xoa-nha-cung-cap` từng id (không có API bulk). */
export async function xoaNhieuNhaCungCap(ids: NhaCungCapId[]): Promise<XoaNhieuNhaCungCapResult> {
  const failed: XoaNhieuNhaCungCapResult['failed'] = []
  let ok = 0
  for (const id of ids) {
    try {
      await xoaNhaCungCap(id)
      ok += 1
    } catch (e) {
      failed.push({
        id: String(id),
        message: (e as { message?: string })?.message ?? '',
      })
    }
  }
  return { ok, failed }
}

export function chuyenTrangThaiNhaCungCap(id: NhaCungCapId, trangThai: NhaCungCapTrangThai) {
  return apiRequest(
    ApisServiceApp.patch(`${PREFIX}/${id}/chuyen-trang-thai-nha-cung-cap`, { trangThai }),
  )
}

export async function taiFileMauNhapNhaCungCapBlob(): Promise<Blob> {
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PREFIX}/tai-file-mau-nhap-nha-cung-cap`, blobResponseConfig),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

export function nhapDanhSachNhaCungCap(
  rows: INhaCungCapImportRow[],
  options?: { capNhatDuNoCuoi?: boolean },
) {
  return apiRequest(
    ApisServiceApp.post(`${PREFIX}/nha-cung-cap-nhap-danh-sach`, rows, {
      params: options?.capNhatDuNoCuoi ? { capNhatDuNoCuoi: true } : undefined,
    }),
  )
}

export function nhapNhaCungCapTuExcel(file: File, options?: { capNhatDuNoCuoi?: boolean }) {
  return apiRequest(
    postMultipartFile(`${PREFIX}/nhap-nha-cung-cap-tu-excel`, file, {
      capNhatDuNoCuoi: options?.capNhatDuNoCuoi,
    }),
  )
}

export async function xuatDanhSachNhaCungCapBlob(search: string): Promise<Blob> {
  const params = nhaCungCapListQueryFromSearch(search)
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PREFIX}/xuat-danh-sach-nha-cung-cap`, { ...blobResponseConfig, params }),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}
